import { addHours, startOfMinute } from 'date-fns';

// Buckets describe the time REMAINING until an order expires ("expiring soon"),
// derived from each order's `expired_at`. Resolved into server-side
// `expired_at__gte`/`expired_at__lte` range params so bucketing stays correct
// across every page, not just the rows currently loaded on the client.
export const ExpiryBucket = {
  Within6h: '0-6h',
  Within12h: '6-12h',
  Within24h: '12-24h',
  Within48h: '24-48h',
  Within72h: '48-72h',
  Beyond72h: '>72h',
} as const;

export type ExpiryBucket = (typeof ExpiryBucket)[keyof typeof ExpiryBucket];

const EXPIRY_BUCKET_VALUES = new Set<string>(Object.values(ExpiryBucket));

export const isExpiryBucket = (value: string | undefined): value is ExpiryBucket =>
  !!value && EXPIRY_BUCKET_VALUES.has(value);

export const EXPIRY_BUCKET_ITEMS: { value: ExpiryBucket | ''; name: string }[] = [
  { value: '', name: 'Any expiry' },
  { value: ExpiryBucket.Within6h, name: 'Expires in 0-6h' },
  { value: ExpiryBucket.Within12h, name: 'Expires in 6-12h' },
  { value: ExpiryBucket.Within24h, name: 'Expires in 12-24h' },
  { value: ExpiryBucket.Within48h, name: 'Expires in 24-48h' },
  { value: ExpiryBucket.Within72h, name: 'Expires in 48-72h' },
  { value: ExpiryBucket.Beyond72h, name: 'Expires in >72h' },
];

type ExpiryRange = { expired_at__gte?: string; expired_at__lte?: string };

// Anchored to the start of the current minute so the resolved ISO strings stay
// stable within a minute — otherwise the Tanstack query key would change on
// every render and trigger needless refetches.
export const resolveExpiryRange = (bucket: ExpiryBucket | '' | undefined): ExpiryRange => {
  if (!bucket) return {};

  const now = startOfMinute(new Date());
  const at = (hours: number) => addHours(now, hours).toISOString();

  switch (bucket) {
    case ExpiryBucket.Within6h:
      return { expired_at__gte: now.toISOString(), expired_at__lte: at(6) };
    case ExpiryBucket.Within12h:
      return { expired_at__gte: at(6), expired_at__lte: at(12) };
    case ExpiryBucket.Within24h:
      return { expired_at__gte: at(12), expired_at__lte: at(24) };
    case ExpiryBucket.Within48h:
      return { expired_at__gte: at(24), expired_at__lte: at(48) };
    case ExpiryBucket.Within72h:
      return { expired_at__gte: at(48), expired_at__lte: at(72) };
    case ExpiryBucket.Beyond72h:
      return { expired_at__gte: at(72) };
  }
};
